import React from 'react';
import { Box } from '@mui/material';
import clsx from 'clsx';
import { TableCell } from './TableCell';
import { getStickySx } from '../libs';
import { mergeCellStyles } from './mergeCellStyles';
import { Column, RenderCellOptions } from '../types';

interface Props<Data extends Record<string, any>, Context extends any = any> {
  context: Context;
  tableData: Data[];
  columns: Column<Data, Context>[];
  /**
   * Absolute-index offset applied to each row's local index before it is
   * exposed as `rowIndex` inside `renderCell`. Required when rendering rows
   * from a slice of a larger dataset (e.g. one of several groups) whose cells
   * reference react-hook-form paths like `bodyFields.${rowIndex}`.
   */
  indexOffset?: number;
}

export const Rows = <Data extends Record<string, any>, Context extends any = any>({
  tableData,
  columns,
  context,
  indexOffset = 0,
}: Props<Data, Context>) => {
  return (
    <>
      {tableData.map((row, localIndex) => {
        const rowIndex = indexOffset + localIndex;

        return (
          <Box key={row?.id ?? `row_${rowIndex}`} className={clsx('listing-section', 'listing-body')}>
            {columns.map((column, columnIndex) => {
              const renderOptions = {
                row,
                column,
                columns,
                context,
                rowIndex,
                columnIndex,
                value: row[column.renderKey] || row,
                existingColumnsTypes: columns.map(({ columnType }) => columnType).filter(Boolean) as string[],
              } as RenderCellOptions<Data, Context>;

              return (
                <TableCell
                  key={`${column.renderKey.toString()}_${rowIndex}_${columnIndex}`}
                  column={column}
                  context={context}
                  sx={{
                    ...getStickySx(column, columns, columnIndex),
                    ...mergeCellStyles(column?.cellStyles, column?.bodyStyles?.(renderOptions)),
                  }}
                >
                  {column.renderCell
                    ? column.renderCell(renderOptions)
                    : column?.formatter
                      ? column.formatter(row[column.renderKey])
                      : row[column.renderKey]}
                </TableCell>
              );
            })}
          </Box>
        );
      })}
    </>
  );
};
